import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Phone, MessageCircle, Settings, X } from 'lucide-react';
import { mockCompanyInfo } from '../mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'בית', href: '#home' },
    { name: 'רכבים', href: '#cars' },
    { name: 'שירותים', href: '#services' },
    { name: 'אודות', href: '#about' },
    { name: 'צור קשר', href: '#contact' }
  ];

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass py-2' 
          : 'bg-gradient-to-b from-black/80 to-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center h-20 md:h-24">
          {/* Logo */}
          <motion.div 
            className="flex items-center cursor-pointer" 
            onClick={() => handleNavClick('#home')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img 
              src="/images/jclass-logo.png" 
              alt="J CLASS MOTORS" 
              className="h-16 w-auto md:h-20 object-contain"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigation.map((item, index) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="relative px-5 py-2 text-luxury-text/80 hover:text-luxury-gold transition-colors duration-300 font-medium text-sm tracking-wide uppercase"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                {item.name}
                <motion.span 
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-luxury-gold"
                  whileHover={{ width: "60%" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            ))}
          </nav>

          {/* Contact Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => window.open(`tel:${mockCompanyInfo.phone}`, '_self')}
                variant="outline"
                size="sm"
                className="border-luxury-border text-luxury-text hover:border-luxury-gold hover:text-luxury-gold bg-transparent transition-all duration-300 rounded-none px-4"
              >
                <Phone className="w-4 h-4 ml-2" />
                {mockCompanyInfo.phone}
              </Button>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => {
                  const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/972${phoneNumber.substring(1)}`, '_blank');
                }}
                size="sm"
                className="bg-green-600 hover:bg-green-500 text-white font-medium transition-all duration-300 rounded-none px-4"
              >
                <MessageCircle className="w-4 h-4 ml-2" />
                וואטסאפ
              </Button>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => window.location.href = '/admin'}
                size="sm"
                className="bg-luxury-surface hover:bg-luxury-surface-light text-luxury-muted hover:text-luxury-text border border-luxury-border transition-all duration-300 rounded-none px-4"
                title="כניסה לפאנל ניהול האתר"
              >
                <Settings className="w-4 h-4 ml-2" />
                ניהול
              </Button>
            </motion.div>
          </div>

          {/* Mobile Menu Trigger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <motion.button 
                className="p-2 text-luxury-text"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu className="w-7 h-7" />
              </motion.button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="bg-luxury-black border-l border-luxury-border w-full max-w-sm z-50 p-0"
            >
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex items-center justify-between p-6 border-b border-luxury-border">
                  <img 
                    src="/images/jclass-logo.png" 
                    alt="J CLASS MOTORS" 
                    className="h-12 w-auto object-contain"
                  />
                </div>
                
                {/* Navigation Links */}
                <nav className="flex-1 py-8 px-6">
                  <div className="flex flex-col gap-2">
                    {navigation.map((item, index) => (
                      <motion.button
                        key={item.name}
                        onClick={() => {
                          handleNavClick(item.href);
                          setMobileMenuOpen(false);
                        }}
                        className="text-right text-luxury-text hover:text-luxury-gold transition-colors duration-300 font-medium text-xl py-4 border-b border-luxury-border/50 hover:border-luxury-gold/50"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {item.name}
                      </motion.button>
                    ))}
                  </div>
                </nav>

                {/* Mobile Contact Buttons */}
                <div className="p-6 border-t border-luxury-border space-y-3">
                  <Button
                    onClick={() => {
                      window.open(`tel:${mockCompanyInfo.phone}`, '_self');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-luxury-gold hover:bg-luxury-gold-hover text-black font-bold transition-all duration-300 rounded-none py-6"
                  >
                    <Phone className="w-5 h-5 ml-2" />
                    {mockCompanyInfo.phone}
                  </Button>
                  <Button
                    onClick={() => {
                      const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
                      window.open(`https://wa.me/972${phoneNumber.substring(1)}`, '_blank');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-green-600 hover:bg-green-500 text-white font-medium transition-all duration-300 rounded-none py-6"
                  >
                    <MessageCircle className="w-5 h-5 ml-2" />
                    וואטסאפ
                  </Button>
                  <Button
                    onClick={() => {
                      window.location.href = '/admin';
                      setMobileMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-luxury-border text-luxury-muted hover:text-luxury-text hover:border-luxury-gold transition-all duration-300 rounded-none py-6"
                  >
                    <Settings className="w-5 h-5 ml-2" />
                    פאנל ניהול
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
